'use client'

import { Page, Card, TextField, Button, BlockStack, Text, Box } from '@shopify/polaris'
import { useState, useCallback } from 'react'
import { redirectToUrl } from '@/app/(app)/hooks/useAppBridgeRedirect'

const APP_URL = process.env.NEXT_PUBLIC_APP_URL || 'https://app.sigmasense.pk'

export default function SignInPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)
      if (!email.trim() || !password) {
        setError('Email and password are required')
        return
      }
      setLoading(true)
      try {
        const res = await fetch(`${APP_URL.replace(/\/$/, '')}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim(), password }),
          credentials: 'include',
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          setError(data?.error || 'Sign in failed')
          return
        }
        const redirect = data?.redirect || `${APP_URL.replace(/\/$/, '')}/dashboard`
        redirectToUrl(redirect)
      } catch (err) {
        setError('Sign in failed. Please try again.')
      } finally {
        setLoading(false)
      }
    },
    [email, password]
  )

  return (
    <Page title="Sign in">
      <Box maxWidth="400px" paddingBlockStart="800">
        <Card>
          <BlockStack gap="400">
            <Text as="h1" variant="headingLg">
              Sigma Sense
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              Sign in with your email and password to access the app.
            </Text>
            <form onSubmit={handleSubmit}>
              <BlockStack gap="400">
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  autoComplete="email"
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={setPassword}
                  autoComplete="current-password"
                />
                {error && (
                  <Text as="p" variant="bodyMd" tone="critical">
                    {error}
                  </Text>
                )}
                <Button submit variant="primary" loading={loading}>
                  Sign in
                </Button>
              </BlockStack>
            </form>
            <Text as="p" variant="bodySm" tone="subdued">
              Don&apos;t have direct access? Install the app from Shopify first, then set it up in Settings.
            </Text>
          </BlockStack>
        </Card>
      </Box>
    </Page>
  )
}
